// src/pages/reports/SalesSummary.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";


function formatDDMMYYYY(ymd) {
  if (!ymd) return "";
  // expects yyyy-mm-dd
  const [yyyy, mm, dd] = ymd.split("-");
  if (!yyyy || !mm || !dd) return ymd;
  return `${dd}-${mm}-${yyyy}`;
}




function normalizeDate(value) {
  if (!value) return "";

  // Firestore Timestamp
  if (value?.seconds) {
    const d = new Date(value.seconds * 1000);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  // JS Date
  if (value instanceof Date) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  // already yyyy-mm-dd string
  return String(value);
}


function todayYMD() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

function daysAgoYMD(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}



function convertDDMMYYYYtoYMD(dateStr) {
  if (!dateStr) return "";
  // accept either "11-12-2025" or "2025-12-11" safely
  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts[0].length === 4) {
      // already YYYY-MM-DD
      return dateStr;
    }
    // assume DD-MM-YYYY
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  return dateStr;
}




function formatNumber(v) {
  // Plain numbers as requested (no thousands separator)
  return Number(v || 0).toFixed(2);
}

function todayDateOnly() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function SalesSummary() {
  // filters
  const [fromDate, setFromDate] = useState("");


  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");

  // data
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]); // aggregated rows
  const [allRows, setAllRows] = useState([]); // full aggregated for export
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // totals
  const [totals, setTotals] = useState({
    BasicAmount: 0,
    GST_Amount: 0,
    CessAmount: 0,
    AddlCessAmount: 0,
    ewayAmount: 0,
    RebateAmount: 0,
    GrandTotal: 0,
  });

  useEffect(() => {
    fetchAndAggregate();     // auto fetch when component loads AND when dates change
  }, [fromDate, toDate]);


  // Fetch orders in date range and aggregate using orderLines
  const fetchAndAggregate = async (opts = {}) => {
    try {
      setLoading(true);
      setPage(1);

      const from = opts.fromDate || fromDate;
      const to = opts.toDate || toDate;

      // Firestore query: status == "APPROVED" and orderDateOnly between
      // Note: ensure composite index on (status, orderDateOnly)
      const ordersRef = collection(db, "orders");
      const q = query(
        collection(db, "orders"),
        where("status", "==", "APPROVED"),
        where("approvedDateOnly", ">=", fromDate),
        where("approvedDateOnly", "<=", toDate)
      );

      const snap = await getDocs(q);
      const rowsMap = new Map(); // key: `${date}__${partyName}`

      // iterate orders and expand frozen orderLines
      snap.docs.forEach((docSnap) => {
        const o = docSnap.data();

        // ensure orderLines exists
        const lines = Array.isArray(o.orderLines) ? o.orderLines : [];

        // party type/name
        const partyName = o.partyName || o.name || o.partyId || "";
        const partyType = o.partyType || o.partytype || o.partyCategory || "";
        const creditTerm = o.creditTerm;

        lines.forEach((L) => {
          // Use frozen date if present, else fallback to order.orderDateOnly
          const date = normalizeDate(o.approvedAt);


          const key = `${date}__${partyName}`;

          // per-line values (we expect these to be frozen numbers)
          const basicAmount = Number(L.basicAmount || 0);
          const gstAmount = Number(L.gstAmount || 0);
          const cessAmount = Number(L.cessAmount || 0);
          const addlCessAmount = Number(L.addlCessAmount || 0);
          const ewayAmount = Number(L.ewayAmount || 0);
          const rebateAmount = Number(L.rebateAmount || 0);
          const grandTotal = ewayAmount + rebateAmount;

          if (!rowsMap.has(key)) {
            rowsMap.set(key, {
              date,
              partyType,
              partyName,
              BasicAmount: 0,
              GST_Amount: 0,
              CessAmount: 0,
              AddlCessAmount: 0,
              ewayAmount: 0,
              RebateAmount: 0,
              GrandTotal: 0,
            });
          }

          const g = rowsMap.get(key);
          g.BasicAmount += basicAmount;
          g.GST_Amount += gstAmount;
          g.CessAmount += cessAmount;
          g.AddlCessAmount += addlCessAmount;
          g.ewayAmount += ewayAmount;
          g.RebateAmount += rebateAmount;
          g.GrandTotal += grandTotal;
        });
      });

      // convert map to array and sort by date, partyName
      const rows = Array.from(rowsMap.values()).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return a.partyName.localeCompare(b.partyName);
      });

      // set state
      setAllRows(rows);
      setGroups(rows.slice(0, PAGE_SIZE));
      computeTotals(rows);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      alert("Error fetching orders for reports. See console.");
    } finally {
      setLoading(false);
    }
  };

  const computeTotals = (rows) => {
    const t = {
      BasicAmount: 0,
      GST_Amount: 0,
      CessAmount: 0,
      AddlCessAmount: 0,
      ewayAmount: 0,
      RebateAmount: 0,
      GrandTotal: 0,
    };

    rows.forEach((r) => {
      t.BasicAmount += Number(r.BasicAmount || 0);
      t.GST_Amount += Number(r.GST_Amount || 0);
      t.CessAmount += Number(r.CessAmount || 0);
      t.AddlCessAmount += Number(r.AddlCessAmount || 0);
      t.ewayAmount += Number(r.ewayAmount || 0);
      t.RebateAmount += Number(r.RebateAmount || 0);
      t.GrandTotal += Number(r.GrandTotal || 0);
    });

    setTotals(t);
  };

  // when search text changes or page changes, compute visible groups
  const visibleRows = useMemo(() => {
    const txt = (searchText || "").trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      if (!txt) return true;
      // search partyName, partyType, date fields
      return (
        (r.partyName || "").toLowerCase().includes(txt) ||
        (r.partyType || "").toLowerCase().includes(txt) ||
        (r.date || "").toLowerCase().includes(txt)
      );
    });

    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [allRows, searchText, page]);

  // re-calc groups shown when visibleRows changes
  useEffect(() => {
    setGroups(visibleRows);
  }, [visibleRows]);

  // ACTIONS
  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };

  const onFilterApply = async () => {
    await fetchAndAggregate({ fromDate: fromDate, toDate: toDate });
  };

  const onExportExcel = () => {
    // Export full allRows (not just page)
    const exportRows = allRows.map((r) => ({
      Date: r.date,
      DistributorType: r.partyType,
      creditTerm: r.creditTerm,
      DistributorName: r.partyName,
      BasicAmount: Number(r.BasicAmount || 0),
      GST_Amount: Number(r.GST_Amount || 0),
      CessAmount: Number(r.CessAmount || 0),
      AddlCessAmount: Number(r.AddlCessAmount || 0),
      ewayAmount: Number(r.ewayAmount || 0),
      RebateAmount: Number(r.RebateAmount || 0),
      GrandTotal: Number(r.GrandTotal || 0),
    }));

    // Build worksheet & workbook using xlsx
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SalesSummary");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `SalesSummary_${fromDate}_to_${toDate}.xlsx`);
  };

  const onPrev = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const onNext = () => {
    const totalPages = Math.ceil(
      (allRows.filter((r) =>
        searchText
          ? (r.partyName || "").toLowerCase().includes(searchText.toLowerCase()) ||
          (r.partyType || "").toLowerCase().includes(searchText.toLowerCase()) ||
          (r.date || "").toLowerCase().includes(searchText.toLowerCase())
          : true
      ).length || 0) / PAGE_SIZE
    );
    setPage((p) => Math.min(totalPages || 1, p + 1));
  };

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Sales Performance Matrix</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Aggregated financial summary by partner entity and operational audit cycle.</p>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
                <button
                    onClick={onExportExcel}
                    className="btn-secondary"
                    style={{ background: "var(--surface)", display: "flex", alignItems: "center", gap: 8 }}
                >
                    <span style={{ color: "var(--success)" }}>Export Audit</span>
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface)", padding: "0 16px", borderRadius: 12, border: "1px solid var(--border)", height: 48 }}>
                    <input 
                        type="date" 
                        value={fromDate} 
                        onChange={e => setFromDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "var(--text-main)", outline: "none" }}
                    />
                    <span style={{ color: "var(--text-muted)", fontWeight: 800 }}>→</span>
                    <input 
                        type="date" 
                        value={toDate} 
                        onChange={e => setToDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "var(--text-main)", outline: "none" }}
                    />
                    <button 
                        onClick={onFilterApply} 
                        style={{ marginLeft: 16, border: "none", background: "var(--primary)", color: "white", padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                    >
                        APPLY
                    </button>
                </div>
            </div>
        </div>

        {/* SEARCH BAR */}
        <div className="premium-card" style={{ padding: 12, marginBottom: 32, background: "var(--surface-hover)", border: "1px solid var(--border)" }}>
            <input
                type="text"
                className="form-input"
                placeholder="Search by Partner Name, Classification, or Date..."
                value={searchText}
                onChange={onSearch}
                style={{ width: "100%", height: 56, fontSize: 16, background: "var(--surface)", border: "none" }}
            />
        </div>

        {/* DATA GRID */}
        <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Financial Ledger</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--primary)", background: "var(--surface-hover)", padding: "4px 12px", borderRadius: 20 }}>PAGE {page}</span>
            </div>

            <div style={{ overflowX: "auto" }}>
                <table className="enterprise-table">
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: 24 }}>OPERATIONAL DATE</th>
                            <th>PARTNER CLASSIFICATION</th>
                            <th>ENTITY NAME</th>
                            <th style={{ textAlign: "right" }}>TAXABLE VAL</th>
                            <th style={{ textAlign: "right" }}>TAX VAL</th>
                            <th style={{ textAlign: "right" }}>CESS VAL</th>
                            <th style={{ textAlign: "right" }}>ADDL CESS</th>
                            <th style={{ textAlign: "right" }}>REBATE</th>
                            <th style={{ textAlign: "right", paddingRight: 24 }}>NET RECEIVABLE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="9" style={{ padding: 100, textAlign: "center" }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--primary)" }}>DECRYPTING SALES MATRIX...</div>
                                </td>
                            </tr>
                        ) : groups.length === 0 ? (
                            <tr>
                                <td colSpan="9" style={{ padding: 80, textAlign: "center", color: "var(--text-muted)" }}>
                                    No aggregated records found for the selected audit range.
                                </td>
                            </tr>
                        ) : (
                            groups.map((r, idx) => (
                                <tr key={idx}>
                                    <td style={{ paddingLeft: 24, fontSize: 12, fontWeight: 700 }}>{formatDDMMYYYY(r.date)}</td>
                                    <td>
                                        <span style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", background: "var(--surface-hover)", padding: "4px 8px", borderRadius: 4 }}>
                                            {r.partyType || "UNCLASSIFIED"}
                                        </span>
                                    </td>
                                    <td style={{ fontWeight: 800, color: "var(--text-main)" }}>{r.partyName}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.BasicAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.GST_Amount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.CessAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.AddlCessAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right", color: "var(--error)" }}>₹{Number(r.RebateAmount || 0).toLocaleString()}</td>
                                    <td style={{ textAlign: "right", paddingRight: 24, fontWeight: 900, color: "var(--primary)" }}>₹{Number(r.GrandTotal).toLocaleString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot style={{ background: "var(--surface-hover)", borderTop: "2px solid var(--border)" }}>
                        <tr style={{ fontWeight: 900 }}>
                            <td colSpan="3" style={{ textAlign: "right", padding: 20 }}>AGGREGATED REVENUE:</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.BasicAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.GST_Amount).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.CessAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.AddlCessAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right", color: "var(--error)" }}>₹{Number(totals.RebateAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right", paddingRight: 24, fontSize: 18, color: "var(--primary)" }}>₹{Number(totals.GrandTotal).toLocaleString()}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        {/* PAGINATION */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 32 }}>
            <button 
                onClick={onPrev}
                className="btn-secondary"
                disabled={page === 1}
                style={{ height: 40, width: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                ←
            </button>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-main)" }}>PAGE {page}</div>
            <button 
                onClick={onNext}
                className="btn-secondary"
                disabled={groups.length < PAGE_SIZE}
                style={{ height: 40, width: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                →
            </button>
        </div>
      </div>
    </Layout>
  );
}
